import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { setStoredToken } from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { Lock, Mail, ArrowRight, Loader2, Database } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const emailId = 'sa-email';
  const passwordId = 'sa-password';
  const errorId = 'sa-error';

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('auth/login', { email, password });

      // Store JWT token for subsequent requests
      const data = response.data;
      if (data.accessToken) {
        setStoredToken(data.accessToken);
      }

      const refreshToken = data.refreshToken;
      const isFirstLogin = data.isFirstLogin;

      const userData = data.user || { email: data.email, role: data.role, userId: data.userId };
      if (userData) {
        login(userData, refreshToken);
        navigate(isFirstLogin ? '/profile' : '/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.title || 'Invalid credentials or server unavailable.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4 relative overflow-hidden">
      {/* Abstract Background Decor */}
      <div className="absolute top-0 left-0 w-full h-full opacity-20 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary-600 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-indigo-600 rounded-full blur-[120px]"></div>
      </div>

      <div className="max-w-md w-full relative">
        <div className="text-center mb-12 animate-fade-in">
          <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-primary-500/20">
            <Database className="w-8 h-8 text-slate-900" />
          </div>
          <h1 className="text-4xl font-black text-white tracking-tight">BIZCORE<span className="text-primary-500">.</span></h1>
          <p className="text-slate-400 mt-3 font-medium">Sign in to BizCore Admin</p>
        </div>

        <div className="bg-white/10 backdrop-blur-2xl p-6 sm:p-10 rounded-[2.5rem] border border-white/10 shadow-2xl animate-fade-in delay-100">
          {error && (
            <div role="alert" id={errorId} className="mb-8 p-4 bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs font-bold rounded-2xl flex items-center">
              <span className="mr-2">⚠️</span> {error}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-6" noValidate>
            <div className="space-y-2">
              <label htmlFor={emailId} className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  id={emailId}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-slate-600 focus:ring-2 focus:ring-primary-500 focus:bg-white/10 transition-all outline-none font-medium"
                  placeholder="admin@bizcore.com"
                  autoComplete="email"
                  aria-invalid={!!error}
                  aria-describedby={error ? errorId : undefined}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor={passwordId} className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  id={passwordId}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-slate-600 focus:ring-2 focus:ring-primary-500 focus:bg-white/10 transition-all outline-none font-medium"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  aria-invalid={!!error}
                  aria-describedby={error ? errorId : undefined}
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-5 bg-white text-slate-950 font-black rounded-2xl shadow-xl hover:bg-slate-100 hover:-translate-y-0.5 transition-all disabled:opacity-50 flex items-center justify-center space-x-3 uppercase tracking-wider text-sm"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                   <span>Sign in</span>
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          <div className="mt-10 text-center">
            <p className="text-[10px] text-slate-600 uppercase tracking-widest">
              BizCore Platform v2.0
            </p>
          </div>
        </div>
        
        <p className="text-center mt-12 text-slate-600 text-[10px] font-bold uppercase tracking-[0.3em]">
          BizCore Admin Portal v2.0
        </p>
      </div>
    </div>
  );
};

export default Login;
